import { useState, useEffect } from "react";
import { useCollection } from "./useCollection";

// Sorting logic
const sorter = (assets) => {
  return assets
    ? assets.sort((a, b) => {
        const aUpVotes = a.upVote || 0;
        const aDownVotes = a.downVote || 0;
        const bUpVotes = b.upVote || 0;
        const bDownVotes = b.downVote || 0;

        const aScore = aUpVotes - aDownVotes;
        const bScore = bUpVotes - bDownVotes;

        return bScore - aScore;
      })
    : null;
};

/**
 * Generic source hook
 * @param {string} collectionName - Firestore collection name ("vtec_assets", "ai_features", etc.)
 */
export const useSources = (collectionName = "vtec_assets", enabled = true) => {
  const [combinedAssets, setCombinedAssets] = useState(null);
  const [reviewAssets, setReviewAssets] = useState(null);
  const [error] = useState(null);
  const [pending] = useState(false);
  const { documents: rawDocs } = useCollection(collectionName, enabled);
  const { documents: vtecAssetsUnsorted } = useCollection("vtec_assets", enabled);
  const [mergedAssets, setMergedAssets] = useState(null);

  useEffect(() => {
    if (rawDocs) {
      // filter: must have a Title
      const hasTitles = rawDocs.filter((doc) => doc.Title);

      // split between review and non-review
      const noReview = hasTitles.filter(
        (doc) => !doc.status || doc.status !== "review"
      );
      const needReview = hasTitles.filter(
        (doc) => doc.status && doc.status === "review"
      );

      // Collection-specific required fields
      let filtered;
      let sorted;
      if (collectionName === "vtec_assets") {
        filtered = noReview.filter(
          (doc) => doc.Title && doc.Heading && doc.Image && doc.Type
        );
        sorted = sorter(filtered);
      } else if (collectionName === "ai_features") {
        filtered = hasTitles
          .filter((doc) => doc.Title)
          .map((doc) => ({
            ...doc,
            Type: "Ai Feature",
          }));
        sorted = filtered;
      } else {
        filtered = noReview; // fallback: pass everything
        sorted = noReview;
      }

      setCombinedAssets(sorted);
      setReviewAssets(needReview);
    }
  }, [rawDocs, collectionName]);

  // merge rawDocs + vtecAssetsUnsorted
  useEffect(() => {
    if (rawDocs && vtecAssetsUnsorted) {
      const merged = [
        ...rawDocs.map((doc) => ({
          ...doc,
          Type: "Ai Feature",
        })),
        ...vtecAssetsUnsorted,
      ];

      // Sort descending (latest first)
      const sorted = merged.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return aTime - bTime;
      });

      setMergedAssets(sorted);
    }
  }, [rawDocs, vtecAssetsUnsorted]);

  return {
    combinedAssets,
    pending,
    error,
    reviewAssets,
    rawDocs,
    vtecAssetsUnsorted,
    mergedAssets,
  };
};
